import React, { useCallback, useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import client from "../../api";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage } from "src/lib/utils";

type CardPoolType = "AURA" | "VIBE";

interface CardPoolItem {
  name: string;
  available: number;
  assigned: number;
}

interface GenerateCardPoolPayload {
  cardPoolType: CardPoolType;
  cardCount: number;
}

interface CardPoolSummaryResponse {
  [key: string]: {
    available: number;
    assigned: number;
  };
}

const DEFAULT_FORM: GenerateCardPoolPayload = {
  cardPoolType: "AURA",
  cardCount: 1,
};

const CardPoolManagement = () => {
  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);

  const isMounted = useIsMounted();

  const { canView, canList, canAdd } = usePermissions(
    permissionConstants.CARD_POOL
  );
  const canViewPool = canView || canList;

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [cardPools, setCardPools] = useState<CardPoolItem[]>([]);
  const [form, setForm] = useState<GenerateCardPoolPayload>(DEFAULT_FORM);

  const showError = useCallback(
    (error: any) => {
      if (error?.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error?.response) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }

      setIsAlertOpened(true);
    },
    [setAlertText, setIsAlertOpened],
  );

  const resetModal = () => {
    setShowGenerateModal(false);
    setForm(DEFAULT_FORM);
  };

  const { mutate: getCardPoolSummary, isLoading } = useMutation(
    client.cardPoolManagement.getPoolSummary,
    {
      onSuccess: (res: any) => {
        const poolData: CardPoolSummaryResponse | undefined = res?.data?.data;

        if (!poolData) {
          setCardPools([]);
          return;
        }

        const formatted = Object.entries(poolData).map(([key, value]) => ({
          name: key,
          available: value.available,
          assigned: value.assigned,
        }));

        setCardPools(formatted);
      },

      onError: showError,
    },
  );

  const { mutate: generateCardPool, isLoading: isGeneratingPool } = useMutation(
    client.cardPoolManagement.generateCardPool,
    {
      onSuccess: () => {
        setAlertText("Card pool generated successfully");
        setIsAlertOpened(true);
        resetModal();
        getCardPoolSummary();
      },

      onError: showError,
    },
  );

  const handleGenerate = () => {
    if (!canAdd) {
      setAlertText("You do not have permission to generate card pools");
      setIsAlertOpened(true);
      return;
    }

    if (!form.cardCount || form.cardCount < 1) {
      setAlertText("Card count must be greater than 0");

      setIsAlertOpened(true);

      return;
    }

    generateCardPool({
      cardPoolType: form.cardPoolType,
      cardCount: Number(form.cardCount),
    });
  };

  useEffect(() => {
    if (!isMounted || !canViewPool) return;

    getCardPoolSummary();
  }, [isMounted, canViewPool, getCardPoolSummary]);

  return (
    <div className="p-6">
      {/* HEADER */}

      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-2xl font-bold">Card Pool Summary</h2>

        <PermissionGate
          module={[permissionConstants.CARD_POOL]}
          action="can_add"
        >
          <button
            onClick={() => setShowGenerateModal(true)}
            className="rounded-xl bg-blue-600 px-5 py-3 text-white transition hover:bg-blue-700"
          >
            + Generate Pool
          </button>
        </PermissionGate>
      </div>

      {!canViewPool ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view card pool summary.
          </p>
        </div>
      ) : (
        <>
          {/* CARDS */}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {cardPools.map((card) => (
          <div
            key={card.name}
            className="rounded-2xl border border-gray-100 bg-white p-6 shadow-md transition hover:shadow-xl"
          >
            <h3 className="mb-5 text-xl font-bold capitalize">{card.name}</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-green-50 p-4">
                <p className="text-sm text-gray-500">Available</p>

                <h4 className="mt-2 text-3xl font-bold text-green-600">
                  {card.available}
                </h4>
              </div>

              <div className="rounded-xl bg-orange-50 p-4">
                <p className="text-sm text-gray-500">Assigned</p>

                <h4 className="mt-2 text-3xl font-bold text-orange-600">
                  {card.assigned}
                </h4>
              </div>
            </div>

            <div className="mt-5 border-t pt-4">
              <p className="text-sm text-gray-500">Total Cards</p>

              <p className="text-lg font-semibold">
                {card.available + card.assigned}
              </p>
            </div>
          </div>
        ))}
      </div>

          {!cardPools.length && !isLoading && (
            <div className="mt-8 text-center text-gray-500">
              No card pools found
            </div>
          )}
        </>
      )}

      {/* MODAL */}

      {showGenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-xl font-bold">Generate Card Pool</h3>

              <button onClick={resetModal}>✕</button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-sm">Card Type</label>

                <select
                  value={form.cardPoolType}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      cardPoolType: e.target.value as CardPoolType,
                    }))
                  }
                  className="w-full rounded-xl border p-3"
                >
                  <option value="AURA">AURA</option>

                  <option value="VIBE">VIBE</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm">Card Count</label>

                <input
                  type="number"
                  min={1}
                  value={form.cardCount}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      cardCount: Math.max(1, Number(e.target.value)),
                    }))
                  }
                  className="w-full rounded-xl border p-3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={resetModal}
                  className="flex-1 rounded-xl border py-3"
                >
                  Cancel
                </button>

                <button
                  disabled={isGeneratingPool}
                  onClick={handleGenerate}
                  className="flex-1 rounded-xl bg-blue-600 py-3 text-white disabled:opacity-50"
                >
                  {isGeneratingPool ? "Generating..." : "Generate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardPoolManagement;
